/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { externalKeysVerifyGcpKms } from "../funcs/externalKeysVerifyGcpKms.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { VerifyKmsKeyResult } from "../models/components/verifykmskeyresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  VerifyGcpKmsKeyRequest,
  VerifyGcpKmsKeySecurity,
} from "../models/operations/verifygcpkmskey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type VerifyGcpKmsKeyMutationVariables = {
  request: VerifyGcpKmsKeyRequest;
  security?: VerifyGcpKmsKeySecurity | undefined;
  options?: RequestOptions;
};

export type VerifyGcpKmsKeyMutationData = VerifyKmsKeyResult;

export type VerifyGcpKmsKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * verifyGcpKmsKey externalKeys
 *
 * @remarks
 * Probe that Speakeasy can reach a GCP KMS external key through its backing credential and use it to sign: read the key's public half, confirm its algorithm matches the one recorded, sign a probe digest, and verify that signature locally against the public half. Performs a real signing operation, which is billed to the key's owner and lands in their Cloud Audit Log. Ephemeral: nothing is persisted. Rate limited per organization. Requires org:admin.
 */
export function useVerifyGcpKmsKeyMutation(
  options?: MutationHookOptions<
    VerifyGcpKmsKeyMutationData,
    VerifyGcpKmsKeyMutationError,
    VerifyGcpKmsKeyMutationVariables
  >,
): UseMutationResult<
  VerifyGcpKmsKeyMutationData,
  VerifyGcpKmsKeyMutationError,
  VerifyGcpKmsKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildVerifyGcpKmsKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyVerifyGcpKmsKey(): MutationKey {
  return ["@gram/client", "externalKeys", "verifyGcpKms"];
}

export function buildVerifyGcpKmsKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: VerifyGcpKmsKeyMutationVariables,
  ) => Promise<VerifyGcpKmsKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyVerifyGcpKmsKey(),
    mutationFn: function verifyGcpKmsKeyMutationFn({
      request,
      security,
      options,
    }): Promise<VerifyGcpKmsKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(externalKeysVerifyGcpKms(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
